import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

interface PlatformKpiProps {
  label: string;
  value: string | number;
  delta?: string;
  trend?: "up" | "down" | "flat";
  icon: LucideIcon;
  accent?: "primary" | "accent" | "success" | "warning" | "destructive";
  hint?: string;
}

const accentMap = {
  primary: "from-primary/15 to-primary/5 text-primary border-primary/20",
  accent: "from-accent/15 to-accent/5 text-accent border-accent/20",
  success: "from-success/15 to-success/5 text-success border-success/20",
  warning: "from-warning/15 to-warning/5 text-warning border-warning/20",
  destructive: "from-destructive/15 to-destructive/5 text-destructive border-destructive/20",
};

export function PlatformKpi({ label, value, delta, trend, icon: Icon, accent = "primary", hint }: PlatformKpiProps) {
  return (
    <Card className="group relative overflow-hidden p-4 transition-all hover:shadow-md">
      <div className={cn("absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r", accentMap[accent])} />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="mt-2 font-display text-2xl font-bold tracking-tight">{value}</div>
          {(delta || hint) && (
            <div className="mt-1.5 flex items-center gap-2 text-[11px]">
              {delta && (
                <span className={cn(
                  "font-medium",
                  trend === "up" && "text-success",
                  trend === "down" && "text-destructive",
                  trend === "flat" && "text-muted-foreground",
                )}>
                  {delta}
                </span>
              )}
              {hint && <span className="text-muted-foreground">{hint}</span>}
            </div>
          )}
        </div>
        <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg border bg-gradient-to-br", accentMap[accent])}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}
